import React, { useState } from 'react';
import { Gift, Check, Copy, ArrowRight } from 'lucide-react';
import useReveal from '../hooks/useReveal';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const AMOUNTS = [10, 20, 30, 50, 100];

export default function GiftCards() {
  useReveal([]);
  const [amount, setAmount] = useState(10);
  const [form, setForm] = useState({ buyerName: '', buyerEmail: '', recipientName: '', message: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [card, setCard] = useState(null);
  const [copied, setCopied] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      const res = await fetch(`${BACKEND}/api/gift-cards`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount, ...form }),
      });
      if (!res.ok) throw new Error('Could not create gift card');
      setCard(await res.json());
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setBusy(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(card.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (card) {
    return (
      <div className="bg-[#E9EBED] min-h-screen">
        <div className="pt-52 sm:pt-56" />
        <section className="pb-24">
          <div className="max-w-xl mx-auto px-5 sm:px-8 reveal">
            <div className="rounded-[2rem] bg-white p-8 sm:p-10 text-center shadow-xl" data-testid="gift-confirmation">
              <span className="inline-flex w-16 h-16 rounded-full bg-[#2C3A28] text-[#EDEAE0] items-center justify-center">
                <Check size={30} />
              </span>
              <h1 className="mt-6 font-display text-3xl sm:text-4xl font-semibold uppercase text-[#2C3A28]">
                Your gift card is ready
              </h1>
              <p className="mt-3 text-[#2C3A28]/70">
                A ${card.amount} AL NATURALE gift card{card.recipientName ? ` for ${card.recipientName}` : ''}. Present this code in store to redeem.
              </p>
              <div className="mt-6 rounded-2xl bg-[#2C3A28] text-[#EDEAE0] px-6 py-7">
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#EDEAE0]/70">Gift card code</p>
                <p className="mt-2 font-display text-3xl sm:text-4xl font-semibold tracking-wider" data-testid="gift-code">{card.code}</p>
                <p className="mt-2 text-[#EDEAE0]/80 text-sm">Balance: ${card.balance}</p>
              </div>
              <button onClick={copyCode} data-testid="copy-code-btn" className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#EDEAE0] text-[#2C3A28] px-6 py-3 font-semibold hover:bg-[#dcd7c7] transition-colors">
                {copied ? <><Check size={17} /> Copied</> : <><Copy size={17} /> Copy code</>}
              </button>
              <p className="mt-6 text-xs text-[#2C3A28]/50 leading-relaxed">
                Note: online payment is coming soon — for now this code is issued directly. Please take a screenshot or copy the code to keep it safe.
              </p>
              <button onClick={() => { setCard(null); setForm({ buyerName: '', buyerEmail: '', recipientName: '', message: '' }); }} className="mt-4 block mx-auto text-sm font-semibold text-[#2C3A28] hover:underline">
                Buy another gift card
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="bg-[#E9EBED]">
      <div className="pt-52 sm:pt-56" />
      <section className="pb-24">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className="reveal text-center max-w-2xl mx-auto">
            <span className="inline-flex items-center gap-2 text-[#2C3A28] text-sm font-semibold uppercase tracking-[0.28em]">
              <Gift size={16} /> e-Gift Cards
            </span>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl font-semibold uppercase text-[#2C3A28]">
              An AL NATURALE gift card
            </h1>
            <p className="mt-4 text-[#2C3A28]/70 text-lg">
              Give the gift of fresh nut-milk coffee and all-day brunch. Choose an amount, add a personal note, and we'll generate a unique code to use in store.
            </p>
          </div>

          <form onSubmit={submit} className="reveal mt-12 grid lg:grid-cols-[1.1fr_1fr] gap-8 items-start">
            {/* amount picker */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-[#2C3A28]">Choose an amount</p>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
                {AMOUNTS.map((a) => (
                  <button
                    key={a}
                    type="button"
                    onClick={() => setAmount(a)}
                    data-testid={`gift-amount-${a}`}
                    className={`rounded-2xl border-2 px-4 py-8 text-center transition-colors ${amount === a ? 'border-[#2C3A28] bg-[#2C3A28] text-[#EDEAE0]' : 'border-[#2C3A28]/15 bg-[#EDEAE0] text-[#2C3A28] hover:border-[#2C3A28]/40'}`}
                  >
                    <span className="font-display text-3xl font-semibold">${a}</span>
                    <span className="block text-xs mt-1 opacity-70">gift card</span>
                  </button>
                ))}
              </div>
            </div>

            {/* details */}
            <div className="rounded-[1.5rem] bg-[#EDEAE0] p-6 shadow-sm space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-[#2C3A28]">Your name</label>
                <input required value={form.buyerName} onChange={(e) => set('buyerName', e.target.value)} data-testid="gift-buyer-name" className="mt-1 w-full rounded-xl border border-[#2C3A28]/15 px-3 py-2.5 text-[#2C3A28] outline-none focus:border-[#2C3A28]" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-[#2C3A28]">Your email (optional)</label>
                <input type="email" value={form.buyerEmail} onChange={(e) => set('buyerEmail', e.target.value)} data-testid="gift-buyer-email" className="mt-1 w-full rounded-xl border border-[#2C3A28]/15 px-3 py-2.5 text-[#2C3A28] outline-none focus:border-[#2C3A28]" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-[#2C3A28]">Recipient name (optional)</label>
                <input value={form.recipientName} onChange={(e) => set('recipientName', e.target.value)} data-testid="gift-recipient-name" className="mt-1 w-full rounded-xl border border-[#2C3A28]/15 px-3 py-2.5 text-[#2C3A28] outline-none focus:border-[#2C3A28]" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-[#2C3A28]">Personal message (optional)</label>
                <textarea rows={3} value={form.message} onChange={(e) => set('message', e.target.value)} data-testid="gift-message" className="mt-1 w-full rounded-xl border border-[#2C3A28]/15 px-3 py-2.5 text-[#2C3A28] outline-none focus:border-[#2C3A28]" />
              </div>
              {err && <p className="text-sm text-red-700" data-testid="gift-error">{err}</p>}
              <button type="submit" disabled={busy} data-testid="gift-submit" className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#2C3A28] text-white px-7 py-4 font-semibold hover:bg-[#1f2a1c] transition-all disabled:opacity-60">
                {busy ? 'Creating…' : <>Get my ${amount} gift card <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" /></>}
              </button>
              <p className="text-xs text-[#2C3A28]/50 text-center">Online payment coming soon — your code is issued instantly for now.</p>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
